﻿import { FileText, Info } from "lucide-react";
import { useT } from "@/lib/i18n";
import { Link } from "react-router-dom";

import { DashboardPage } from "@/components/dashboard-page";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LegalOverrideEditor } from "@/features/legal/override-editor";
import { LEGAL_PAGE_SLUGS, type LegalPageSlug } from "@ticketnjoy/types";

/**
 * Yasal sayfa override editörü — 5 sekme, her biri kendi TipTap instance'ı.
 * Tab değişince editör unmount olur; undo history ve draft state taze başlar.
 * Böylece sekmeler arası geçişte yanlışlıkla diğer sayfanın draft'ı kalmaz.
 */
export function LegalOverridesPage() {
  return (
    <DashboardPage>
      <LegalOverridesView />
    </DashboardPage>
  );
}

function LegalOverridesView() {
  const t = useT("settings.legalOverrides");
  const tNav = useT("nav");

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/settings"
          className="inline-flex text-xs text-muted-foreground hover:text-foreground"
        >
          <span className="me-1 inline-block rtl:-scale-x-100">←</span>
          {tNav("settings")}
        </Link>
        <div className="flex items-center gap-2 pt-1">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">{t("title")}</h1>
        </div>
        <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 dark:border-sky-900/50 dark:bg-sky-950/50 dark:text-sky-200">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p>{t("intro")}</p>
          <p className="text-sky-700 dark:text-sky-300/90">
            {t("legalNotice")}
          </p>
        </div>
      </div>

      <Tabs defaultValue={LEGAL_PAGE_SLUGS[0]} className="space-y-4">
        {/* Mobile'da yatay scroll; horizontal swipe ile erişilebilir. */}
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex h-auto w-max gap-1 p-1">
            {LEGAL_PAGE_SLUGS.map((slug) => (
              <TabsTrigger key={slug} value={slug} className="text-xs">
                {t(`pages.${slug}` as const)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {LEGAL_PAGE_SLUGS.map((slug) => (
          <TabsContent key={slug} value={slug} className="mt-0">
            <LegalOverrideEditor slug={slug as LegalPageSlug} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
